import { useRef, useState } from "react";
import { Upload, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Label } from "@/components/ui/label";

export default function PhotoUpload({ value, onChange, label = "Photo", testid = "photo-upload", compact = false }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const pick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Only JPEG, PNG or WebP images are allowed");
            e.target.value = "";
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image must be under 8 MB");
            e.target.value = "";
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/upload", fd);
            onChange(data.url);
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    if (compact) {
        return (
            <div className="flex items-center gap-2">
                {value ? (
                    <img
                        src={value}
                        alt=""
                        data-testid={`${testid}-preview`}
                        className="w-10 h-10 rounded-lg object-cover object-top border border-[#262638]"
                    />
                ) : (
                    <div
                        data-testid={`${testid}-preview`}
                        className="w-10 h-10 rounded-lg bg-[#1A1A28] border border-dashed border-[#262638] flex items-center justify-center"
                    >
                        <User className="w-4 h-4 text-slate-600" />
                    </div>
                )}
                <button
                    type="button"
                    data-testid={`${testid}-button`}
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="p-2 rounded-lg border border-[#262638] text-slate-400 hover:border-[#FF5500] hover:text-[#FF5500] disabled:opacity-60 transition-colors duration-200"
                    aria-label="Upload photo"
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid={`${testid}-input`}
                    onChange={pick}
                />
            </div>
        );
    }

    return (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
            <div className="mt-2 flex items-center gap-4">
                {value ? (
                    <img
                        src={value}
                        alt={label}
                        data-testid={`${testid}-preview`}
                        className="w-16 h-16 rounded-xl object-cover object-top border border-[#262638]"
                    />
                ) : (
                    <div
                        data-testid={`${testid}-preview`}
                        className="w-16 h-16 rounded-xl bg-[#1A1A28] border border-[#262638] flex items-center justify-center"
                    >
                        <User className="w-6 h-6 text-slate-600" />
                    </div>
                )}
                <button
                    type="button"
                    data-testid={`${testid}-button`}
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 border border-[#262638] hover:border-[#FF5500] hover:text-[#FF5500] disabled:opacity-60 text-slate-300 font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5 rounded-full transition-colors duration-200"
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading…" : value ? "Change photo" : "Upload photo"}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid={`${testid}-input`}
                    onChange={pick}
                />
            </div>
        </div>
    );
}
